import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Form } from "react-bootstrap";
import { Context } from "../App";
import { formatDateDisplayEnglish, toDateInputValue } from "../utils/eventDate";
import { resolveTheatreGuestBounds } from "../utils/slotGuests";
import { saveBookingFlow } from "../utils/bookingFlowStorage";
import { useCms } from "../context/CmsContext";
import "../partyBossTheme.css";

const ADVANCE_INCL_FEE = 700;

const fieldClass =
  "party-boss-form-control rounded-lg border border-white/20 bg-[#12081f] px-2.5 py-1.5 text-sm !text-[#f1f5f9] shadow-[inset_0_0_0_1px_rgba(236,72,153,0.12)] placeholder:text-slate-400 focus:border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-500/35";

const labelClass =
  "mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#c9c1d9]";

const overviewRowClass =
  "flex min-w-0 items-start gap-1.5 px-2 py-1 sm:gap-2 sm:px-3 sm:py-1";

const overviewIconClass =
  "mt-0.5 shrink-0 text-sm leading-none text-[#ffd166] sm:text-base";

const bookingGridClass = "grid gap-x-3 gap-y-2 sm:grid-cols-2 sm:items-start";

/**
 * Step 2: booking details — compact two-column layout with Party Boss styling.
 */
export default function BookingFormPage() {
  const navigate = useNavigate();
  const {
    selectedTheaterByDate,
    context,
    setTotalCost,
    selectedTime,
    theatersList,
    addonMetaData,
  } = useContext(Context); // addonMetaData: default event type for payload (no UI)

  const { cms } = useCms();
  const bf = cms.bookingForm;

  const [selectedTheater, setSelectedTheater] = useState({});
  const [bookingName, setBookingName] = useState("");
  const [persons, setPersons] = useState(2);
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [extraPersonPrice, setExtraPersonPrice] = useState(0);

  useEffect(() => {
    const selectedValue = theatersList?.filter(
      (list) => list.id === selectedTheaterByDate?.id
    );
    setSelectedTheater(selectedValue?.[0] || {});
  }, [theatersList, selectedTheaterByDate]);

  /** Headcount for theatre package pricing and personsCount (theatre min…max from searchSlots). */
  const packageGuests = Number(persons);

  const { minGuests, maxGuests } = useMemo(
    () => resolveTheatreGuestBounds(selectedTheaterByDate),
    [selectedTheaterByDate]
  );

  const personOptions = useMemo(
    () =>
      Array.from({ length: Math.max(1, maxGuests - minGuests + 1) }, (_, i) => minGuests + i),
    [minGuests, maxGuests]
  );

  /** First active celebration type from catalogue — used as eventTypeId; not shown in UI. */
  const resolvedEventTypeId = useMemo(() => {
    const first = addonMetaData?.eventTypes?.find((e) => e?.isActive === "Y");
    return first?.id != null ? String(first.id) : "";
  }, [addonMetaData]);

  useEffect(() => {
    if (!selectedTime) return;
    const maxG = maxGuests;
    const n = Math.min(Math.max(minGuests, packageGuests), maxG);
    if (n > selectedTime.defaultCapacity) {
      const extra =
        (n - selectedTime.defaultCapacity) *
          (selectedTime.extraPerPerson ?? 0) +
        (selectedTime.basePrice ?? 0);
      setExtraPersonPrice(extra);
      setTotalCost(extra);
    } else {
      const base = selectedTime.basePrice ?? 0;
      setExtraPersonPrice(base);
      setTotalCost(base);
    }
  }, [selectedTime, persons, setTotalCost, packageGuests, minGuests, maxGuests]);

  useEffect(() => {
    const p = Number(persons);
    const clamped = Math.min(Math.max(minGuests, p), maxGuests);
    if (clamped !== p) setPersons(clamped);
  }, [minGuests, maxGuests, persons]);

  /** Display-only: slot-time offer is already applied in selectedTime.basePrice (no extra math). */
  const slotOfferBreakdown = useMemo(() => {
    const st = selectedTime;
    if (!st?.slotOfferActive) return null;
    const regular = Number(st.regularSlotBasePrice ?? 0);
    const applied = Number(st.basePrice ?? 0);
    const saving = Math.max(0, Math.round(regular - applied));
    const label = String(st.slotOfferDisplayLabel || "").trim();
    const kind = String(st.slotOfferPriceKind || "RS").toUpperCase();
    const raw = Number(st.slotOfferRawValue ?? 0);
    const pctLabel =
      kind === "PCT" && Number.isFinite(raw) && raw > 0
        ? `${Math.round(raw)}% off listed base`
        : "";
    const headline = String(st.slotOfferHeadline || "").trim();
    return { regular, applied, saving, label, pctLabel, headline };
  }, [selectedTime]);

  /** Single display line: "DD MON YYYY" e.g. 05 May 2026 */
  const overviewDate = useMemo(
    () => formatDateDisplayEnglish(toDateInputValue(context?.eventDate)),
    [context?.eventDate]
  );

  const lineTotal = extraPersonPrice ?? 0;
  const balanceAtVenue = Math.max(0, lineTotal - ADVANCE_INCL_FEE);

  const slotLabel = String(selectedTime?.slotTime || "—").trim();

  const onSubmit = (e) => {
    e.preventDefault();
    if (!resolvedEventTypeId) return;
    saveBookingFlow({
      slotId: Number(selectedTime?.id),
      eventId: Number(resolvedEventTypeId),
      personsCount: Number(persons),
      bookingName: bookingName.trim(),
      whatsapp: whatsapp.trim(),
      email: email.trim(),
      eventDate: toDateInputValue(context?.eventDate),
      theatreBaseCost: lineTotal,
      selectedTheaterSnapshot: {
        id: selectedTheaterByDate?.id,
        name: selectedTheaterByDate?.name,
      },
      selectedTimeSnapshot: {
        id: selectedTime?.id,
        defaultCapacity: selectedTime?.defaultCapacity,
        extraPerPerson: selectedTime?.extraPerPerson,
        basePrice: selectedTime?.basePrice,
        slotTime: selectedTime?.slotTime,
      },
      slotOffer:
        selectedTime?.slotOfferActive
          ? {
              active: true,
              headline: selectedTime.slotOfferHeadline || "",
              offerBasePrice: Number(selectedTime.basePrice ?? 0),
              regularSlotBasePrice: Number(
                selectedTime.regularSlotBasePrice ?? 0
              ),
              offerPriceKind: selectedTime.slotOfferPriceKind || "RS",
              offerRawValue: Number(selectedTime.slotOfferRawValue ?? 0),
              slotOfferDisplayLabel:
                selectedTime.slotOfferDisplayLabel ||
                "",
              id: selectedTime.slotTimeOfferId ?? null,
            }
          : null,
    });
    try {
      sessionStorage.removeItem("pb_review_back_path");
    } catch (_) {
      /* ignore */
    }
    navigate("/booknow/addons");
  };

  if (!selectedTheaterByDate?.id || !selectedTime?.id) {
    return (
      <section className="party-boss-page w-full bg-[#05020a] pb-16 pt-8 text-white md:pt-10">
        <div className="party-boss-bg relative z-10 mx-auto max-w-lg px-4 text-center">
          <p className="text-[#c9c1d9]">
            {bf.missingSlotMessage}
          </p>
          <button
            type="button"
            className="mt-4 rounded-full border border-white/20 bg-white/10 px-6 py-2 font-bold"
            onClick={() => navigate("/booknow")}
          >
            {bf.backToTheatres}
          </button>
        </div>
      </section>
    );
  }

  const canProceed =
    (selectedTheater?.availableSlots ?? 0) > 0 &&
    bookingName.trim().length > 0 &&
    /^\d{10}$/.test(whatsapp.trim()) &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    Number(persons) >= minGuests &&
    Number(persons) <= maxGuests &&
    resolvedEventTypeId !== "";

  const cardShell =
    "overflow-hidden rounded-2xl border border-white/15 bg-[#0a0614] shadow-[0_12px_40px_-16px_rgba(0,0,0,0.45)] ring-1 ring-white/[0.06]";

  return (
    <section className="party-boss-page w-full bg-[#05020a] pb-12 pt-6 text-white selection:bg-pink-500/40 md:pb-16 md:pt-8">
      <div className="party-boss-bg relative z-10 px-3 sm:px-4 md:px-6">
        <p className="text-center text-[10px] font-black uppercase tracking-widest text-[#ffd166] sm:text-xs">
          {bf.pageEyebrow}
        </p>
        <h1 className="mb-1 text-center text-xl font-black text-white md:text-3xl">
          {bf.pageTitle}
        </h1>
        <p className="mx-auto mb-4 max-w-xl text-center text-xs text-[#c9c1d9] sm:text-sm">
          {bf.pageSubtitle}
        </p>

        <Container className="mx-auto max-w-4xl p-0">
          <Form id="booking-details-form" onSubmit={onSubmit}>
            <div className="grid gap-4 md:grid-cols-2 md:items-start">
              <div className="min-w-0">
                <div className={cardShell}>
                  <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/25 to-violet-700/25 px-3 py-2.5 sm:px-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                      {bf.overviewLabel}
                    </p>
                  </div>
                  <div className="divide-y divide-white/10 bg-[#12081f]/40 text-sm">
                    <div className={overviewRowClass}>
                      <i
                        className={`bi bi-building ${overviewIconClass}`}
                        aria-hidden
                      />
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                        {bf.overviewTheatreLabel}
                      </span>
                      <span className="min-w-0 flex-1 break-words text-xs font-semibold leading-snug text-white sm:text-sm">
                        {selectedTheaterByDate?.name || "—"}
                      </span>
                    </div>
                    <div className={overviewRowClass}>
                      <i
                        className={`bi bi-calendar3 ${overviewIconClass}`}
                        aria-hidden
                      />
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                        {bf.overviewDateLabel}
                      </span>
                      <span className="min-w-0 flex-1 text-xs font-semibold leading-snug text-[#ffd166] sm:text-sm">
                        {overviewDate || "—"}
                      </span>
                    </div>
                    <div className={overviewRowClass}>
                      <i
                        className={`bi bi-clock ${overviewIconClass}`}
                        aria-hidden
                      />
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                        {bf.overviewSlotLabel}
                      </span>
                      <span className="min-w-0 flex-1 whitespace-pre-line break-words text-xs font-semibold leading-snug text-white sm:text-sm">
                        {slotLabel}
                        {slotOfferBreakdown?.headline ? (
                          <span className="mt-1 block font-semibold leading-snug text-red-400">
                            {slotOfferBreakdown.headline}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-white/10">
                    <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/15 to-violet-700/15 px-3 py-2 sm:px-4">
                      <h2 className="text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                        {bf.bookingDetailsSectionTitle}
                      </h2>
                    </div>
                    <div className="px-3 pb-3 pt-2.5 sm:px-4 sm:pb-4 sm:pt-3">
                    <div className={bookingGridClass}>
                    <div className="min-w-0 sm:col-span-2">
                      <Form.Label className={labelClass}>{bf.bookingNameLabel}</Form.Label>
                      <Form.Control
                        className={fieldClass}
                        value={bookingName}
                        onChange={(e) => setBookingName(e.target.value)}
                        placeholder={bf.bookingNamePlaceholder}
                        maxLength={40}
                        required
                      />
                    </div>

                    <div className="min-w-0 sm:col-span-2">
                      <Form.Label className={labelClass}>{bf.personsLabel}</Form.Label>
                      <Form.Select
                        className={fieldClass}
                        value={persons}
                        onChange={(e) =>
                          setPersons(Number(e.target.value) || 1)
                        }
                        required
                      >
                        {personOptions.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </div>

                    <div className="min-w-0">
                      <Form.Label className={labelClass}>{bf.whatsappLabel}</Form.Label>
                      <Form.Control
                        className={fieldClass}
                        type="tel"
                        inputMode="numeric"
                        pattern="\d{10}"
                        maxLength={10}
                        placeholder={bf.whatsappPlaceholder}
                        value={whatsapp}
                        onChange={(e) =>
                          setWhatsapp(
                            e.target.value.replace(/\D/g, "").slice(0, 10)
                          )
                        }
                        required
                      />
                    </div>

                    <div className="min-w-0">
                      <Form.Label className={labelClass}>{bf.emailLabel}</Form.Label>
                      <Form.Control
                        className={fieldClass}
                        type="email"
                        placeholder={bf.emailPlaceholder}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>

                    </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="min-w-0">
                <div className={`${cardShell} sticky top-20`}>
                  <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/25 to-violet-700/25 px-3 py-2.5 sm:px-4">
                    <h3 className="text-center text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                      {bf.summaryTitle}
                    </h3>
                  </div>
                  <div className="space-y-2 px-3 py-3 text-sm sm:px-4">
                    <div className="flex justify-between gap-2 text-[#e8e3f0]">
                      <span className="text-xs">{bf.summaryPersons}</span>
                      <span className="text-right text-xs font-bold text-white">
                        {persons}
                      </span>
                    </div>
                    {slotOfferBreakdown ? (
                      <div className="rounded-lg border border-red-500/40 bg-red-950/25 px-2.5 py-2 text-[11px] leading-snug text-red-100/95">
                        <p className="m-0 mb-1 text-[10px] font-black uppercase tracking-wide text-red-300">
                          Time-slot offer
                        </p>
                        {slotOfferBreakdown.headline ? (
                          <p className="m-0 mb-2 text-xs font-medium text-red-200">
                            {slotOfferBreakdown.headline}
                          </p>
                        ) : null}
                        <div className="space-y-1 text-[#fecaca]">
                          <div className="flex justify-between gap-2">
                            <span className="text-[#c9c1d9]">Listed slot base</span>
                            <span className="font-semibold text-white">
                              <i className="bi bi-currency-rupee" />
                              {slotOfferBreakdown.regular}
                            </span>
                          </div>
                          {(slotOfferBreakdown.label || slotOfferBreakdown.pctLabel) ? (
                            <div className="flex justify-between gap-2 text-[10px]">
                              <span className="text-[#c9c1d9]">Discount</span>
                              <span className="max-w-[58%] text-end font-semibold text-amber-200">
                                {slotOfferBreakdown.label || slotOfferBreakdown.pctLabel}
                              </span>
                            </div>
                          ) : null}
                          {slotOfferBreakdown.saving > 0 ? (
                            <div className="flex justify-between gap-2 font-bold text-emerald-300">
                              <span>Saved on package base</span>
                              <span>
                                −<i className="bi bi-currency-rupee" />
                                {slotOfferBreakdown.saving}
                              </span>
                            </div>
                          ) : null}
                          <div className="flex justify-between gap-2 border-t border-white/15 pt-1.5 text-xs text-white">
                            <span className="text-[#94a3b8]">Package base after offer</span>
                            <span className="font-bold">
                              <i className="bi bi-currency-rupee" />
                              {slotOfferBreakdown.applied}
                            </span>
                          </div>
                        </div>
                        <p className="m-0 mt-2 text-[9px] text-[#94a3b8]">
                          Totals below use this base — no extra discount is applied on this page.
                        </p>
                      </div>
                    ) : null}
                    <div className="flex justify-between gap-2 text-[#e8e3f0]">
                      <span className="text-xs">{bf.summaryPackage}</span>
                      <span className="font-bold text-white">
                        <i className="bi bi-currency-rupee" />
                        {extraPersonPrice ?? 0}
                      </span>
                    </div>
                    <hr className="my-1 border-white/10" />
                    <div className="flex justify-between gap-2 text-base font-black text-white">
                      <span>{bf.summaryTotal}</span>
                      <span className="text-[#ffd166]">
                        <i className="bi bi-currency-rupee" />
                        {lineTotal}
                      </span>
                    </div>
                    <hr className="my-1 border-white/10" />
                    <div className="flex justify-between gap-2 text-xs text-[#c9c1d9]">
                      <span>{bf.summaryAdvance}</span>
                      <span className="font-bold text-[#ffd166]">
                        <i className="bi bi-currency-rupee" />
                        {ADVANCE_INCL_FEE}
                      </span>
                    </div>
                    <p className="text-[10px] leading-snug text-[#94a3b8]">
                      {bf.summaryAdvanceNote}
                    </p>
                    <hr className="my-1 border-white/10" />
                    <div className="flex justify-between gap-2 text-xs text-[#e8e3f0]">
                      <span>{bf.summaryBalance}</span>
                      <span className="font-bold text-emerald-400">
                        <i className="bi bi-currency-rupee" />
                        {balanceAtVenue}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 border-t border-white/10 px-3 py-3 sm:px-4">
                    <button
                      type="button"
                      className="flex-1 rounded-xl border border-white/20 bg-white/5 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
                      onClick={() => navigate("/booknow")}
                    >
                      {bf.backLabel}
                    </button>
                    <button
                      type="submit"
                      form="booking-details-form"
                      className="flex-[1.35] rounded-xl bg-gradient-to-r from-pink-500 to-violet-600 py-2.5 text-sm font-black text-white shadow-md disabled:cursor-not-allowed disabled:opacity-45"
                      disabled={!canProceed}
                    >
                      {bf.proceedLabel}
                    </button>
                  </div>
                  <p className="border-t border-white/10 px-3 py-2.5 text-center text-[10px] leading-snug text-[#94a3b8] sm:px-4">
                    {bf.summaryFootnote}
                  </p>
                  <p className="border-t border-white/5 px-3 pb-3 text-center text-[10px] text-emerald-400/90 sm:px-4">
                    {selectedTheater?.availableSlots ?? 0} {bf.slotsLeftSuffix}{" "}
                    <span className="text-[#ffd166]">{overviewDate}</span>
                  </p>
                </div>
              </div>
            </div>
          </Form>
        </Container>
      </div>
    </section>
  );
}
